import type { AudioEngine } from './AudioEngine'

/**
 * The sound library. Each entry is a recipe, not a sample — parameters like
 * impact power feed straight into pitch, brightness and length, so the mix
 * tells you how hard something hit before you look at the screen.
 */

/** Body impact: a noise crack layered over a descending "thud" sine. */
export function sfxHit(audio: AudioEngine, power: number, pan: number): void {
  // Normalised against the top of the launch table rather than 16 m/s. On the
  // old scale everything from a scuff upward arrived at full volume, so the ear
  // got no information about how hard the hit was — the one channel that reads
  // faster than the eye was saying nothing. See docs/PHYSICS.md §6.4.
  const strength = Math.min(1, power / 40)

  audio.noise({
    duration: 0.09 + strength * 0.07,
    gain: 0.18 + strength * 0.3,
    filter: 'bandpass',
    freq: 1800 + strength * 1400,
    freqEnd: 340,
    q: 1.1,
    attack: 0.001,
    release: 0.5,
    pan,
  })

  audio.tone({
    type: 'sine',
    freq: 110 + strength * 70,
    freqEnd: 38,
    duration: 0.16 + strength * 0.12,
    gain: 0.24 + strength * 0.3,
    attack: 0.002,
    release: 0.7,
    pan,
  })
}

/** A blocked hit: dull, metallic, obviously absorbed. */
export function sfxBraceBlock(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'square',
    freq: 220,
    freqEnd: 150,
    duration: 0.1,
    gain: 0.16,
    attack: 0.001,
    release: 0.6,
    pan,
  })
  audio.noise({
    duration: 0.14,
    gain: 0.12,
    filter: 'lowpass',
    freq: 900,
    freqEnd: 260,
    attack: 0.002,
    pan,
  })
}

/** Push windup: a short upward whip. */
export function sfxPush(audio: AudioEngine, pan: number): void {
  audio.noise({
    duration: 0.13,
    gain: 0.16,
    filter: 'bandpass',
    freq: 500,
    freqEnd: 2600,
    q: 3.5,
    attack: 0.004,
    release: 0.7,
    pan,
  })
}

/** Dash: filtered air rushing past. */
export function sfxDash(audio: AudioEngine, pan: number): void {
  audio.noise({
    duration: 0.22,
    gain: 0.2,
    filter: 'bandpass',
    freq: 900,
    freqEnd: 260,
    q: 5,
    attack: 0.005,
    release: 0.75,
    pan,
    rate: 1.4,
  })
}

/** Shockwave: a falling sweep with an expanding noise skirt. */
export function sfxWave(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'sawtooth',
    freq: 420,
    freqEnd: 58,
    duration: 0.5,
    gain: 0.2,
    attack: 0.006,
    release: 0.8,
    pan,
  })
  audio.noise({
    duration: 0.55,
    gain: 0.18,
    filter: 'lowpass',
    freq: 3200,
    freqEnd: 300,
    attack: 0.02,
    release: 0.85,
    pan,
  })
}

/**
 * The personal moves.
 *
 * They shared the shockwave and the vortex cues because the audio, like the
 * visuals, was keyed on the *slot*. Six fighters firing the same two sounds is
 * the audible half of "способности у всех одинаковые": the ear learns the cue
 * before the eye learns the animation, and a wrong cue teaches the wrong move.
 *
 * All of them are built from the same three generators as everything else, so
 * they sit in the existing mix rather than next to it.
 */

/** ЗАСЛОН: concrete meeting concrete, dead and heavy. */
export function sfxBarrier(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'square',
    freq: 150,
    freqEnd: 52,
    duration: 0.22,
    gain: 0.17,
    attack: 0.002,
    release: 0.5,
    pan,
  })
  audio.noise({
    duration: 0.34,
    gain: 0.16,
    filter: 'lowpass',
    freq: 1400,
    freqEnd: 180,
    attack: 0.004,
    release: 0.7,
    pan,
  })
}

/** ХОД: a low motor under a slow scrape — mass moving, not speed. */
export function sfxCharge(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'sawtooth',
    freq: 74,
    freqEnd: 96,
    duration: 1.1,
    gain: 0.13,
    attack: 0.08,
    release: 0.9,
    pan,
  })
  audio.noise({
    duration: 1,
    gain: 0.1,
    filter: 'bandpass',
    freq: 380,
    freqEnd: 240,
    q: 2.5,
    attack: 0.1,
    release: 0.9,
    pan,
  })
}

/** ПОДБРОС: a rising whoop. The only cue in the game that goes up. */
export function sfxToss(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'triangle',
    freq: 210,
    freqEnd: 760,
    duration: 0.3,
    gain: 0.17,
    attack: 0.004,
    release: 0.6,
    pan,
  })
  audio.noise({
    duration: 0.26,
    gain: 0.12,
    filter: 'highpass',
    freq: 700,
    freqEnd: 2600,
    attack: 0.006,
    release: 0.6,
    pan,
  })
}

/** ЗАХВАТ: a short hard snap. */
export function sfxLunge(audio: AudioEngine, pan: number): void {
  audio.noise({
    duration: 0.14,
    gain: 0.22,
    filter: 'bandpass',
    freq: 1700,
    freqEnd: 420,
    q: 4,
    attack: 0.002,
    release: 0.5,
    pan,
    rate: 1.6,
  })
}

/** ВЕТЕР: air only. No transient, because it does not hit — it pushes. */
export function sfxGust(audio: AudioEngine, pan: number): void {
  audio.noise({
    duration: 0.6,
    gain: 0.19,
    filter: 'bandpass',
    freq: 620,
    freqEnd: 2100,
    q: 1.1,
    attack: 0.09,
    release: 0.85,
    pan,
  })
}

/** ОТРЫВ: a scuff and a light lift away. */
export function sfxRetreat(audio: AudioEngine, pan: number): void {
  audio.noise({
    duration: 0.2,
    gain: 0.16,
    filter: 'highpass',
    freq: 900,
    freqEnd: 2400,
    attack: 0.004,
    release: 0.6,
    pan,
    rate: 1.5,
  })
  audio.tone({
    type: 'triangle',
    freq: 300,
    freqEnd: 520,
    duration: 0.2,
    gain: 0.1,
    attack: 0.004,
    release: 0.6,
    pan,
  })
}

/** СБРОС: wood leaving the hands. */
export function sfxCrate(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'square',
    freq: 260,
    freqEnd: 120,
    duration: 0.12,
    gain: 0.14,
    attack: 0.002,
    release: 0.4,
    pan,
  })
  audio.noise({
    duration: 0.18,
    gain: 0.12,
    filter: 'bandpass',
    freq: 800,
    freqEnd: 300,
    q: 2,
    attack: 0.003,
    release: 0.5,
    pan,
  })
}

/** ОБВАЛ: the heaviest cue in the roster — a body hitting the slab. */
export function sfxSlam(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'sine',
    freq: 180,
    freqEnd: 36,
    duration: 0.5,
    gain: 0.26,
    attack: 0.002,
    release: 0.85,
    pan,
  })
  audio.noise({
    duration: 0.5,
    gain: 0.2,
    filter: 'lowpass',
    freq: 2600,
    freqEnd: 160,
    attack: 0.004,
    release: 0.9,
    pan,
  })
}

/** ПОДМЕНА: two clicks and a swallow — something changed places. */
export function sfxSwap(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'sine',
    freq: 880,
    freqEnd: 220,
    duration: 0.16,
    gain: 0.15,
    attack: 0.001,
    release: 0.5,
    pan,
  })
  audio.tone({
    type: 'sine',
    freq: 220,
    freqEnd: 880,
    duration: 0.16,
    gain: 0.15,
    attack: 0.001,
    release: 0.5,
    pan: -pan,
  })
}

/** ПРОКАТ: a long rumble for a long commitment. */
export function sfxRoll(audio: AudioEngine, pan: number): void {
  audio.noise({
    duration: 0.8,
    gain: 0.17,
    filter: 'lowpass',
    freq: 900,
    freqEnd: 220,
    attack: 0.02,
    release: 0.9,
    pan,
  })
}

/** Brace engage: a metal latch closing, then a held hum. */
export function sfxBrace(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'square',
    freq: 640,
    freqEnd: 300,
    duration: 0.07,
    gain: 0.12,
    attack: 0.001,
    release: 0.5,
    pan,
  })
  audio.tone({
    type: 'triangle',
    freq: 92,
    duration: 0.8,
    gain: 0.1,
    attack: 0.05,
    release: 0.9,
    pan,
  })
}

/** Vortex arming: a cluster of detuned saws swelling upward. */
export function sfxVortexArm(audio: AudioEngine, pan: number): void {
  for (const detune of [-14, 0, 11]) {
    audio.tone({
      type: 'sawtooth',
      freq: 70,
      freqEnd: 210,
      duration: 0.55,
      gain: 0.07,
      attack: 0.12,
      release: 0.95,
      detune,
      pan,
    })
  }
}

/** Vortex burst: sub boom, noise slam, long tail. */
export function sfxVortexBurst(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'sine',
    freq: 150,
    freqEnd: 32,
    duration: 0.6,
    gain: 0.4,
    attack: 0.002,
    release: 0.8,
    pan,
  })
  audio.noise({
    duration: 0.45,
    gain: 0.3,
    filter: 'bandpass',
    freq: 2400,
    freqEnd: 180,
    q: 0.7,
    attack: 0.001,
    release: 0.8,
    pan,
  })
}

/** Barrel: a sharp crack, a low boom and a handful of shrapnel ticks. */
export function sfxExplosion(audio: AudioEngine, pan: number): void {
  audio.noise({
    duration: 0.5,
    gain: 0.38,
    filter: 'lowpass',
    freq: 5200,
    freqEnd: 190,
    attack: 0.001,
    release: 0.75,
    pan,
  })
  audio.tone({
    type: 'sine',
    freq: 180,
    freqEnd: 28,
    duration: 0.7,
    gain: 0.42,
    attack: 0.001,
    release: 0.85,
    pan,
  })
  for (let i = 0; i < 5; i++) {
    audio.noise({
      duration: 0.05,
      gain: 0.07,
      filter: 'highpass',
      freq: 2600,
      attack: 0.001,
      release: 0.4,
      delay: 0.08 + i * 0.045,
      pan: pan + (i % 2 === 0 ? 0.25 : -0.25),
    })
  }
}

/** Burner charging: a rising, unstable whine that warns before it fires. */
export function sfxBurnerCharge(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'sawtooth',
    freq: 180,
    freqEnd: 760,
    duration: 0.8,
    gain: 0.09,
    attack: 0.2,
    release: 0.95,
    pan,
  })
}

export function sfxBurnerFire(audio: AudioEngine, pan: number): void {
  audio.noise({
    duration: 0.4,
    gain: 0.3,
    filter: 'highpass',
    freq: 400,
    freqEnd: 2600,
    attack: 0.002,
    release: 0.7,
    pan,
    rate: 1.2,
  })
  audio.tone({
    type: 'sine',
    freq: 90,
    freqEnd: 240,
    duration: 0.3,
    gain: 0.22,
    attack: 0.003,
    release: 0.7,
    pan,
  })
}

/** Falling into the void: pitch and brightness drop away together. */
export function sfxFall(audio: AudioEngine, pan: number): void {
  audio.noise({
    duration: 1.6,
    gain: 0.22,
    filter: 'lowpass',
    freq: 2200,
    freqEnd: 90,
    attack: 0.02,
    release: 0.95,
    pan,
  })
  audio.tone({
    type: 'triangle',
    freq: 320,
    freqEnd: 34,
    duration: 1.5,
    gain: 0.18,
    attack: 0.01,
    release: 0.95,
    pan,
  })
}

/** Countdown beeps: three short, one long. */
export function sfxCountdown(audio: AudioEngine, index: number): void {
  const final = index >= 3
  audio.tone({
    type: 'square',
    freq: final ? 660 : 440,
    duration: final ? 0.45 : 0.1,
    gain: 0.14,
    attack: 0.004,
    release: final ? 0.85 : 0.5,
  })
}

/** Round won: a short rising figure over the drone. */
export function sfxRoundWin(audio: AudioEngine): void {
  const notes = [220, 277, 330]
  notes.forEach((freq, i) => {
    audio.tone({
      type: 'triangle',
      freq,
      duration: 0.5,
      gain: 0.14,
      attack: 0.02,
      release: 0.9,
      delay: i * 0.09,
    })
  })
}

/** Match won: a slow, heavy major swell. */
export function sfxMatchWin(audio: AudioEngine): void {
  const notes = [110, 165, 220, 330]
  notes.forEach((freq, i) => {
    audio.tone({
      type: 'sawtooth',
      freq,
      duration: 2.4,
      gain: 0.09,
      attack: 0.35,
      release: 0.95,
      delay: i * 0.12,
      detune: i * 3,
    })
  })
}

/** A round ending with nobody standing. */
export function sfxDraw(audio: AudioEngine): void {
  audio.tone({
    type: 'sawtooth',
    freq: 180,
    freqEnd: 90,
    duration: 1.1,
    gain: 0.12,
    attack: 0.05,
    release: 0.9,
  })
}

/** The platform starting to come apart. */
export function sfxCollapse(audio: AudioEngine): void {
  audio.tone({
    type: 'sine',
    freq: 60,
    freqEnd: 30,
    duration: 2,
    gain: 0.3,
    attack: 0.3,
    release: 0.9,
  })
  audio.noise({
    duration: 1.8,
    gain: 0.16,
    filter: 'lowpass',
    freq: 700,
    freqEnd: 120,
    attack: 0.2,
    release: 0.9,
  })
}

/** Picking up a buff: a bright rising arpeggio, distinct per kind. */
export function sfxPickup(audio: AudioEngine, kind: 'shield' | 'haste', pan: number): void {
  const notes = kind === 'shield' ? [440, 587, 880] : [523, 784, 1046]
  notes.forEach((freq, i) => {
    audio.tone({
      type: kind === 'shield' ? 'triangle' : 'square',
      freq,
      duration: 0.3,
      gain: 0.11,
      attack: 0.004,
      release: 0.85,
      delay: i * 0.055,
      pan,
    })
  })
}

/**
 * One skip of a launched fighter off the slab — the stone touching the water.
 *
 * Pitched up with each successive contact so the series is audible as a series:
 * three rising scrapes tell you a hit was heavy without you having to watch it.
 */
export function sfxSkip(audio: AudioEngine, speed: number, index: number, pan: number): void {
  const strength = Math.min(1, speed / 40)
  audio.noise({
    duration: 0.09 + strength * 0.06,
    gain: 0.1 + strength * 0.18,
    filter: 'bandpass',
    freq: 900 + index * 500 + strength * 700,
    freqEnd: 260,
    q: 1.6,
    attack: 0.001,
    release: 0.45,
    pan,
  })
  audio.tone({
    type: 'sine',
    freq: 90 + index * 26 + strength * 40,
    freqEnd: 40,
    duration: 0.1,
    gain: 0.1 + strength * 0.14,
    attack: 0.002,
    release: 0.6,
    pan,
  })
}

/** The bolt kissing the floor: short, glassy, low. */
export function sfxBoltSkip(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'triangle',
    freq: 620,
    freqEnd: 320,
    duration: 0.07,
    gain: 0.07,
    attack: 0.001,
    release: 0.5,
    pan,
  })
}

/**
 * The bolt ricocheting off a prop. Distinctly metallic and pitched above every
 * other impact sound, because a ricochet is information: it says the shot is
 * still live and coming from a new direction.
 */
export function sfxRicochet(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'square',
    freq: 1560,
    freqEnd: 880,
    duration: 0.11,
    gain: 0.1,
    attack: 0.001,
    release: 0.4,
    pan,
  })
  audio.noise({
    duration: 0.08,
    gain: 0.09,
    filter: 'highpass',
    freq: 2600,
    attack: 0.001,
    release: 0.4,
    pan,
  })
}

/** The bolt dying with nothing to show for it. */
export function sfxFizzle(audio: AudioEngine, pan: number): void {
  audio.noise({
    duration: 0.16,
    gain: 0.07,
    filter: 'lowpass',
    freq: 1200,
    freqEnd: 220,
    attack: 0.004,
    release: 0.8,
    pan,
  })
}

/**
 * A button pressed with nothing behind it.
 *
 * Dry and quiet by design: it must not be mistakable for an ability, only for
 * an answer. Silence here was read as the game dropping inputs.
 */
export function sfxDenied(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'square',
    freq: 190,
    freqEnd: 120,
    duration: 0.05,
    gain: 0.05,
    attack: 0.001,
    release: 0.4,
    pan,
  })
}

/** The cue for a hit whose trajectory ends over the void. */
export function sfxLaunch(audio: AudioEngine, pan: number): void {
  audio.tone({
    type: 'sawtooth',
    freq: 320,
    freqEnd: 1500,
    duration: 0.3,
    gain: 0.12,
    attack: 0.004,
    release: 0.7,
    pan,
  })
  audio.noise({
    duration: 0.35,
    gain: 0.13,
    filter: 'bandpass',
    freq: 700,
    freqEnd: 3200,
    q: 0.8,
    attack: 0.005,
    release: 0.8,
    pan,
  })
}

/** UI click for menus. */
export function sfxUi(audio: AudioEngine, high = false): void {
  audio.tone({
    type: 'square',
    freq: high ? 720 : 420,
    duration: 0.05,
    gain: 0.08,
    attack: 0.002,
    release: 0.5,
  })
}
